import { appConfig } from "../config";
import { cleanupArCanvases, stopMediaTracksIn, type ManagedArSession } from "../lifecycle/session-lifecycle";
import { bindTargetEvents } from "./ar-events";

type MindArSystem = {
  start?: () => Promise<void> | void;
  stop?: () => Promise<void> | void;
};

type AFrameSceneElement = HTMLElement & {
  hasLoaded?: boolean;
  systems?: Record<string, MindArSystem | undefined>;
  pause?: () => void;
};

export class TargetLoadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TargetLoadError";
  }
}

export interface CreateArSceneOptions {
  container: HTMLElement;
  onTargetFound(): void;
  onTargetLost(): void;
  onCtaClick(): void;
  fetcher?: typeof fetch;
}

export async function createArScene(options: CreateArSceneOptions): Promise<ManagedArSession> {
  await ensureTargetAvailable(appConfig.targetFile, options.fetcher);

  const scene = buildAFrameScene(options);
  options.container.replaceChildren(scene);

  return new AFrameMindArSession(scene, options.container);
}

export async function ensureTargetAvailable(
  targetFile: string = appConfig.targetFile,
  fetcher: typeof fetch = fetch,
): Promise<void> {
  try {
    const response = await fetcher(targetFile, {
      method: "HEAD",
      cache: "no-store",
    });

    if (!response.ok) {
      throw new TargetLoadError(`O alvo ${targetFile} respondeu com HTTP ${response.status}.`);
    }

    const contentLength = response.headers.get("content-length");

    if (contentLength !== null && Number(contentLength) < 16) {
      throw new TargetLoadError(`O alvo ${targetFile} existe, mas parece inválido.`);
    }
  } catch (error) {
    if (error instanceof TargetLoadError) {
      throw error;
    }

    throw new TargetLoadError(`Não foi possível carregar o alvo ${targetFile}.`);
  }
}

export function normalizeCameraConstraints(
  constraints: MediaStreamConstraints | undefined,
): MediaStreamConstraints {
  const input = constraints ?? { video: true };

  if (input.video === false) {
    return input;
  }

  const video =
    input.video === true || input.video === undefined
      ? {}
      : { ...(input.video as MediaTrackConstraints) };

  const facingMode = video.facingMode;

  if (facingMode === undefined || facingMode === "environment") {
    video.facingMode = { ideal: "environment" };
  } else if (typeof facingMode === "object" && facingMode !== null && "exact" in facingMode) {
    const exact = (facingMode as { exact?: unknown }).exact;

    if (exact === "environment") {
      video.facingMode = { ideal: "environment" };
    }
  }

  return {
    ...input,
    audio: false,
    video,
  };
}

function buildAFrameScene(options: CreateArSceneOptions): AFrameSceneElement {
  const scene = createAElement("a-scene") as AFrameSceneElement;
  scene.classList.add("ar-scene");
  scene.setAttribute(
    "mindar-image",
    [
      `imageTargetSrc: ${appConfig.targetFile}`,
      "autoStart: false",
      "filterMinCF: 0.0001",
      "filterBeta: 0.001",
      "warmupTolerance: 5",
      "missTolerance: 8",
      "uiLoading: no",
      "uiScanning: no",
      "uiError: no",
    ].join("; "),
  );
  scene.setAttribute("color-space", "sRGB");
  scene.setAttribute(
    "renderer",
    "colorManagement: true; physicallyCorrectLights: true; antialias: true; alpha: true; precision: mediump",
  );
  scene.setAttribute("vr-mode-ui", "enabled: false");
  scene.setAttribute("device-orientation-permission-ui", "enabled: false");
  scene.setAttribute("embedded", "true");

  const assets = createAElement("a-assets");
  assets.setAttribute("timeout", "10000");

  const logo = document.createElement("img");
  logo.id = "convertoLogo";
  logo.src = appConfig.logoFile;
  logo.alt = "Converto";
  assets.append(logo);

  const camera = createAElement("a-camera");
  camera.setAttribute("position", "0 0 0");
  camera.setAttribute("look-controls", "enabled: false");
  camera.setAttribute("cursor", "fuse: false; rayOrigin: mouse");
  camera.setAttribute("raycaster", "objects: .ar-clickable; far: 8");

  const target = createAElement("a-entity");
  target.setAttribute("mindar-image-target", `targetIndex: ${appConfig.targetIndex}`);
  const unbindTargetEvents = bindTargetEvents(target, {
    onTargetFound: options.onTargetFound,
    onTargetLost: options.onTargetLost,
  });

  target.append(
    createFrame(),
    createLogo(),
    createHeadline(),
    createCapabilityChips(),
    createCta(options.onCtaClick),
  );

  scene.append(assets, camera, target);
  scene.dataset.disposeTargetEvents = "true";
  scene.addEventListener(
    "dispose-target-events",
    () => {
      unbindTargetEvents();
    },
    { once: true },
  );

  return scene;
}

function createFrame(): HTMLElement {
  const frame = createAElement("a-entity");
  frame.setAttribute("position", "0 0 0.018");
  frame.setAttribute("animation__in", "property: scale; from: 0.86 0.86 0.86; to: 1 1 1; dur: 520; easing: easeOutCubic");
  frame.setAttribute("animation__fade", "property: components.material.material.opacity; from: 0; to: 0.92; dur: 520");

  const edges = [
    ["0 0.53 0", "1.05", "0.018"],
    ["0 -0.53 0", "1.05", "0.018"],
    ["-0.53 0 0", "0.018", "1.05"],
    ["0.53 0 0", "0.018", "1.05"],
  ] as const;

  for (const [position, width, height] of edges) {
    const edge = createAElement("a-plane");
    edge.setAttribute("position", position);
    edge.setAttribute("width", width);
    edge.setAttribute("height", height);
    edge.setAttribute("material", "color: #FCCB7A; opacity: 0.82; transparent: true; shader: flat");
    frame.append(edge);
  }

  const panel = createAElement("a-plane");
  panel.setAttribute("position", "0 0 0.002");
  panel.setAttribute("width", "1.02");
  panel.setAttribute("height", "1.02");
  panel.setAttribute("material", "color: #101820; opacity: 0.22; transparent: true; shader: flat");
  frame.append(panel);

  return frame;
}

function createLogo(): HTMLElement {
  const logo = createAElement("a-image");
  logo.setAttribute("src", "#convertoLogo");
  logo.setAttribute("position", "0 0.26 0.075");
  logo.setAttribute("width", "0.54");
  logo.setAttribute("height", "0.18");
  logo.setAttribute("material", "transparent: true; opacity: 0.96");
  logo.setAttribute("animation__float", "property: position; to: 0 0.3 0.075; dur: 1500; dir: alternate; loop: true; easing: easeInOutSine");
  logo.setAttribute("animation__fade", "property: components.material.material.opacity; from: 0; to: 0.96; dur: 600; easing: easeOutCubic");
  return logo;
}

function createHeadline(): HTMLElement {
  const headline = createAElement("a-text");
  headline.setAttribute("value", appConfig.headline);
  headline.setAttribute("align", "center");
  headline.setAttribute("position", "0 0.02 0.08");
  headline.setAttribute("width", "1.18");
  headline.setAttribute("wrap-count", "28");
  headline.setAttribute("color", "#FFFFFF");
  headline.setAttribute("font", "kelsonsans");
  headline.setAttribute("shader", "msdf");
  headline.setAttribute("negate", "false");
  headline.setAttribute("animation__enter", "property: position; from: 0 -0.03 0.08; to: 0 0.02 0.08; dur: 640; easing: easeOutCubic");
  return headline;
}

function createCapabilityChips(): HTMLElement {
  const group = createAElement("a-entity");
  group.setAttribute("position", "0 -0.2 0.08");

  const capabilities = [
    ["IA", "-0.34 0 0"],
    ["Automação", "0 0 0"],
    ["Integração", "0.34 0 0"],
  ] as const;

  for (const [label, position] of capabilities) {
    const chip = createAElement("a-entity");
    chip.setAttribute("position", position);

    const background = createAElement("a-plane");
    background.setAttribute("width", label === "IA" ? "0.22" : "0.34");
    background.setAttribute("height", "0.1");
    background.setAttribute("material", "color: #FE7211; opacity: 0.88; transparent: true; shader: flat");

    const text = createAElement("a-text");
    text.setAttribute("value", label);
    text.setAttribute("align", "center");
    text.setAttribute("position", "0 -0.014 0.01");
    text.setAttribute("width", label === "IA" ? "0.42" : "0.55");
    text.setAttribute("wrap-count", "16");
    text.setAttribute("color", "#FFFFFF");
    text.setAttribute("font", "kelsonsans");
    text.setAttribute("shader", "msdf");
    text.setAttribute("negate", "false");

    chip.append(background, text);
    group.append(chip);
  }

  return group;
}

function createCta(onCtaClick: () => void): HTMLElement {
  const cta = createAElement("a-entity");
  cta.classList.add("ar-clickable");
  cta.setAttribute("position", "0 -0.4 0.09");
  cta.addEventListener("click", onCtaClick);

  const background = createAElement("a-plane");
  background.setAttribute("width", "0.72");
  background.setAttribute("height", "0.14");
  background.setAttribute("material", "color: #FD320E; opacity: 0.95; transparent: true; shader: flat");

  const text = createAElement("a-text");
  text.setAttribute("value", "Falar com a Converto");
  text.setAttribute("align", "center");
  text.setAttribute("position", "0 -0.022 0.012");
  text.setAttribute("width", "0.96");
  text.setAttribute("wrap-count", "24");
  text.setAttribute("color", "#FFFFFF");
  text.setAttribute("font", "kelsonsans");
  text.setAttribute("shader", "msdf");
  text.setAttribute("negate", "false");

  cta.append(background, text);
  return cta;
}

function createAElement(tagName: string): HTMLElement {
  return document.createElement(tagName);
}

class AFrameMindArSession implements ManagedArSession {
  private started = false;

  constructor(
    private readonly scene: AFrameSceneElement,
    private readonly container: HTMLElement,
  ) {}

  async start(): Promise<void> {
    if (this.started) {
      return;
    }

    await waitForSceneLoaded(this.scene);
    await withIdealEnvironmentCamera(async () => {
      const system = getMindArSystem(this.scene);

      if (!system?.start) {
        throw new Error("Sistema MindAR não disponível na cena A-Frame.");
      }

      await system.start();
    });
    this.started = true;
  }

  async stop(): Promise<void> {
    const system = getMindArSystem(this.scene);

    if (system?.stop) {
      await system.stop();
    }

    stopMediaTracksIn(this.container);
    this.started = false;
  }

  destroy(): void {
    this.scene.dispatchEvent(new Event("dispose-target-events"));
    this.scene.pause?.();
    this.scene.remove();
    cleanupArCanvases(this.container);
  }
}

async function waitForSceneLoaded(scene: AFrameSceneElement): Promise<void> {
  if (scene.hasLoaded) {
    return;
  }

  await new Promise<void>((resolve) => {
    scene.addEventListener("loaded", () => resolve(), { once: true });
  });
}

function getMindArSystem(scene: AFrameSceneElement): MindArSystem | undefined {
  return scene.systems?.["mindar-image-system"];
}

async function withIdealEnvironmentCamera<T>(callback: () => Promise<T>): Promise<T> {
  const mediaDevices = navigator.mediaDevices;

  if (!mediaDevices?.getUserMedia) {
    return callback();
  }

  const originalGetUserMedia = mediaDevices.getUserMedia.bind(mediaDevices);
  const patchedGetUserMedia: typeof mediaDevices.getUserMedia = (constraints) =>
    originalGetUserMedia(normalizeCameraConstraints(constraints));

  mediaDevices.getUserMedia = patchedGetUserMedia;

  try {
    return await callback();
  } finally {
    mediaDevices.getUserMedia = originalGetUserMedia;
  }
}
